import type { FieldDto, FieldOptions } from "../../types/field";
import type { FieldPresentation } from "../../types/presentation";
import type { ValuesByName, VisibilityConfig } from "../../types/visibility";
import { readOptionalAge, type BirthdateAgeLimits } from "./dateField";
import { resolveFieldType } from "./normalizeFieldType";
import { parseBoolean } from "./parseBoolean";
import { normalizeVisibilityConfig, normalizeValue } from "./visibility";

const INLINE_CAPABLE_TYPES = new Set([
  "text",
  "email",
  "phone",
  "city",
  "choice",
  "select",
  "date",
  "birthdate",
]);

export function canFieldBeInline(type: string): boolean {
  return INLINE_CAPABLE_TYPES.has(type);
}

export type ChoiceItem = string | { value?: unknown; label?: unknown };

export type ChoiceRow = {
  label: string;
  value: string;
};

export type LabeledChoiceValue = {
  label: string;
  value: string;
};

export function readLabeledChoiceValue(
  raw: unknown,
): LabeledChoiceValue | null {
  if (raw == null) return null;
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed.startsWith("{")) return null;
    try {
      return readLabeledChoiceValue(JSON.parse(trimmed) as unknown);
    } catch {
      return null;
    }
  }
  if (typeof raw !== "object" || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  if ("field_id" in record || "fieldId" in record) return null;
  if (!("value" in record)) return null;
  const labelRaw = record.label ?? record.name;
  if (labelRaw == null) return null;
  const label = String(labelRaw).trim();
  const value = String(record.value ?? "").trim();
  if (!label || !value) return null;
  return { label, value };
}

export function labeledChoiceFromRows(
  rows: ChoiceRow[],
  stored: string,
): LabeledChoiceValue | null {
  const normalized = stored.trim();
  if (!normalized) return null;
  const row = rows.find((item) => item.value === normalized);
  if (!row) return null;
  if (row.label === row.value) return null;
  return { label: row.label, value: row.value };
}

function normalizeChoiceValue(choice: ChoiceItem, index: number): string {
  if (typeof choice === "string") return choice;

  if (choice && typeof choice === "object") {
    if (choice.label != null && String(choice.label).trim() !== "") {
      return String(choice.label).trim();
    }
    if (choice.value != null) {
      return String(choice.value);
    }
  }

  return String(index);
}

/** Submit / visibility value for a choice item (prefer numeric id when present). */
export function getChoiceSubmitValue(
  choice: ChoiceItem,
  index: number,
): string {
  if (typeof choice === "string") return choice;

  if (choice && typeof choice === "object" && choice.value != null) {
    return String(choice.value);
  }

  return normalizeChoiceValue(choice, index);
}

export function getFieldChoices(field: FieldDto): string[] {
  const options = field.options;
  if (Array.isArray(options)) {
    return options.map((choice, index) => normalizeChoiceValue(choice, index));
  }
  if (
    options &&
    typeof options === "object" &&
    Array.isArray(options.choices)
  ) {
    return options.choices.map((choice, index) =>
      normalizeChoiceValue(choice as ChoiceItem, index),
    );
  }
  return [];
}

/** Raw choices for select/choice/city whitelist — keeps value/label objects. */
export function getFieldChoiceItems(field: FieldDto): ChoiceItem[] {
  if (resolveFieldType(field.type) === "city") {
    if (field.city?.choices?.length) {
      return field.city.choices as ChoiceItem[];
    }
    if (
      isFieldOptionsObject(field.options) &&
      Array.isArray(field.options.choices) &&
      field.options.choices.length > 0
    ) {
      return field.options.choices as ChoiceItem[];
    }
    return [];
  }

  const options = field.options;
  if (Array.isArray(options)) return options;
  if (
    options &&
    typeof options === "object" &&
    Array.isArray(options.choices)
  ) {
    return options.choices as ChoiceItem[];
  }
  return [];
}

/** Есть ли whitelist городов (непустой city_ids / choices). */
export function hasCityWhitelist(field: FieldDto): boolean {
  if (resolveFieldType(field.type) !== "city") return false;
  if (field.city?.city_ids && field.city.city_ids.length > 0) return true;
  if (field.city?.choices && field.city.choices.length > 0) return true;
  if (
    isFieldOptionsObject(field.options) &&
    Array.isArray(field.options.city_ids) &&
    field.options.city_ids.length > 0
  ) {
    return true;
  }
  if (
    isFieldOptionsObject(field.options) &&
    Array.isArray(field.options.choices) &&
    field.options.choices.length > 0
  ) {
    return true;
  }
  return false;
}

export function toChoiceRows(items: ChoiceItem[]): ChoiceRow[] {
  return items.map((item, index) => ({
    label: normalizeChoiceValue(item, index),
    value: getChoiceSubmitValue(item, index),
  }));
}

export type ResolvedChoicesBy = {
  field: string;
  map: Record<string, string[]>;
  url?: string;
  path?: string;
  item?: string;
  value?: string;
};

function readOptionalTrimmed(value: unknown): string {
  return value == null ? "" : String(value).trim();
}

function choicesByFromUnknown(raw: unknown): ResolvedChoicesBy | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  const fieldName = readOptionalTrimmed(record.field);
  if (!fieldName) return null;

  const url = readOptionalTrimmed(record.url);
  const path = readOptionalTrimmed(record.path);
  const item = readOptionalTrimmed(record.item);
  const valueKey = readOptionalTrimmed(record.value);
  const map = record.map != null ? normalizeChoicesByMap(record.map) : {};

  if (!url && Object.keys(map).length === 0) return null;

  return {
    field: fieldName,
    map,
    ...(url ? { url } : {}),
    ...(path ? { path } : {}),
    ...(item ? { item } : {}),
    ...(valueKey ? { value: valueKey } : {}),
  };
}

export function resolveChoicesByConfig(
  field: FieldDto,
): ResolvedChoicesBy | null {
  return (
    choicesByFromUnknown(field.select?.choices_by) ??
    (isFieldOptionsObject(field.options)
      ? choicesByFromUnknown(field.options.choices_by)
      : null)
  );
}

export function resolveChoicesUrlConfig(
  field: FieldDto,
): { url: string; path: string; item: string; value: string } | null {
  const options = isFieldOptionsObject(field.options) ? field.options : null;
  const sourceUrl =
    options?.choices_source &&
    typeof options.choices_source === "object" &&
    !Array.isArray(options.choices_source)
      ? readOptionalTrimmed(options.choices_source.url)
      : "";
  const url =
    readOptionalTrimmed(field.select?.choices_url) ||
    readOptionalTrimmed(options?.choices_url) ||
    sourceUrl;
  if (!url) return null;
  return {
    url,
    path:
      readOptionalTrimmed(field.select?.choices_path) ||
      readOptionalTrimmed(options?.choices_path),
    item:
      readOptionalTrimmed(field.select?.choices_item) ||
      readOptionalTrimmed(options?.choices_item),
    value:
      readOptionalTrimmed(field.select?.choices_value) ||
      readOptionalTrimmed(options?.choices_value),
  };
}

export function readChoicesSourceError(field: FieldDto): string | null {
  if (!isFieldOptionsObject(field.options)) return null;
  const source = field.options.choices_source;
  if (!source || typeof source !== "object" || Array.isArray(source)) {
    return null;
  }
  const error = source.error;
  if (error == null) return null;
  const text = String(error).trim();
  return text || null;
}

export function isRemoteChoicesBy(field: FieldDto): boolean {
  return Boolean(resolveChoicesByConfig(field)?.url);
}

function toChoiceStrings(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map((item) => String(item ?? "").trim()).filter(Boolean);
}

/**
 * Нормализует map из API.
 * Ожидается `{ "cityId": ["Лидер", ...] }`.
 * PHP иногда отдаёт `[[...]]` (ключи потеряны) — сохраняем как `"0"`, `"1"`, …
 */
export function normalizeChoicesByMap(map: unknown): Record<string, string[]> {
  const result: Record<string, string[]> = {};

  if (Array.isArray(map)) {
    map.forEach((entry, index) => {
      if (Array.isArray(entry)) {
        // [key, choices[]] — редкий tuple-формат
        if (
          entry.length === 2 &&
          (typeof entry[0] === "string" || typeof entry[0] === "number") &&
          Array.isArray(entry[1])
        ) {
          const choices = toChoiceStrings(entry[1]);
          if (choices.length > 0) {
            result[String(entry[0])] = choices;
          }
          return;
        }

        const choices = toChoiceStrings(entry);
        if (choices.length > 0) {
          result[String(index)] = choices;
        }
        return;
      }

      if (entry && typeof entry === "object") {
        const record = entry as Record<string, unknown>;
        const key =
          record.key ?? record.value ?? record.city_id ?? record.id ?? index;
        const choices = toChoiceStrings(
          record.choices ?? record.values ?? record.list,
        );
        if (choices.length > 0) {
          result[String(key)] = choices;
        }
      }
    });
    return result;
  }

  if (map && typeof map === "object") {
    for (const [key, value] of Object.entries(map)) {
      const choices = toChoiceStrings(value);
      if (choices.length > 0) {
        result[String(key)] = choices;
      }
    }
  }

  return result;
}

/** Ключи 0..n-1 — признак, что PHP отдал map как массив без id городов. */
function isArrayShapedMapKeys(map: Record<string, string[]>): boolean {
  const keys = Object.keys(map);
  if (keys.length === 0) return false;
  return keys.every((key, index) => key === String(index));
}

/** Варианты select с учётом `choices_by` и текущего значения источника. */
export function getSelectChoices(
  field: FieldDto,
  valuesByName: ValuesByName = {},
): string[] {
  const choicesBy = resolveChoicesByConfig(field);
  if (!choicesBy) {
    return getFieldChoiceItems(field).map((item, index) =>
      getChoiceSubmitValue(item, index),
    );
  }

  const sourceValue = normalizeValue(valuesByName[choicesBy.field] ?? "");
  if (sourceValue === "") return [];
  if (choicesBy.url) return [];

  const direct = choicesBy.map[String(sourceValue)];
  if (direct) return direct;

  // Ключи городов потеряны в API (`map: [[...]]`) — одна группа на любой город.
  if (isArrayShapedMapKeys(choicesBy.map)) {
    const groups = Object.values(choicesBy.map);
    if (groups.length === 1) return groups[0] ?? [];
  }

  return [];
}

export function getFieldChoiceRows(
  field: FieldDto,
  valuesByName: ValuesByName = {},
): ChoiceRow[] {
  const choicesBy = resolveChoicesByConfig(field);
  if (choicesBy) {
    if (choicesBy.url) return [];
    return toChoiceRows(getSelectChoices(field, valuesByName));
  }
  return toChoiceRows(getFieldChoiceItems(field));
}

export function getChoicesBySourceField(field: FieldDto): string | null {
  const config = resolveChoicesByConfig(field);
  return config?.field || null;
}

export function resolveFieldVisibility(
  field: FieldDto,
): VisibilityConfig | null {
  if (field.visibility) {
    return normalizeVisibilityConfig(field.visibility);
  }

  const options = field.options;
  if (options && typeof options === "object" && !Array.isArray(options)) {
    const nested = options.visibility;
    if (nested) {
      return normalizeVisibilityConfig(nested);
    }
  }

  return null;
}

export function fieldHasVisibility(field: FieldDto): boolean {
  return resolveFieldVisibility(field) !== null;
}

export function isFieldOptionsObject(
  options: FieldOptions | null | undefined,
): options is Exclude<FieldOptions, string[]> {
  return (
    options != null && typeof options === "object" && !Array.isArray(options)
  );
}

/** `options.filterable` or API `select.filterable` (see FieldResource). */
export function isFieldFilterable(field: FieldDto): boolean {
  if (field.select && parseBoolean(field.select.filterable)) {
    return true;
  }

  const options = field.options;
  if (isFieldOptionsObject(options)) {
    return parseBoolean(options.filterable);
  }

  return false;
}

/** `options.with_time` or the type-specific API block. */
export function isDateWithTime(field: FieldDto): boolean {
  if (
    resolveFieldType(field.type) === "date-range" &&
    field.dateRange?.with_time != null
  ) {
    return parseBoolean(field.dateRange.with_time);
  }
  if (field.date) {
    return parseBoolean(field.date.with_time);
  }

  const options = field.options;
  if (isFieldOptionsObject(options)) {
    return parseBoolean(options.with_time ?? options.withTime);
  }

  return false;
}

/** `options.min_age` / `max_age` or API `birthdate.min_age` / `max_age`. */
export function getBirthdateAgeLimits(field: FieldDto): BirthdateAgeLimits {
  if (field.birthdate) {
    return {
      min_age: readOptionalAge(field.birthdate.min_age),
      max_age: readOptionalAge(field.birthdate.max_age),
    };
  }

  const options = field.options;
  if (isFieldOptionsObject(options)) {
    return {
      min_age: readOptionalAge(options.min_age ?? options.minAge),
      max_age: readOptionalAge(options.max_age ?? options.maxAge),
    };
  }

  return { min_age: null, max_age: null };
}

export function getFieldPresentation(field: FieldDto): FieldPresentation {
  const options = field.options;
  if (
    isFieldOptionsObject(options) &&
    options.presentation === "inline" &&
    canFieldBeInline(field.type)
  ) {
    return "inline";
  }
  return "stacked";
}

export function isFieldInline(field: FieldDto): boolean {
  return getFieldPresentation(field) === "inline";
}
